import React, { useState, useEffect } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import { useCommonForm } from './hooks/useCommonForm';
import ErrorDisplay from './components/ErrorDisplay';
import IntegerInput from './components/IntegerInput';
import api from '@services/api';
import './RefillForm.scss';

const RefillForm = ({
    data,
    refill,
    onSubmit,
    onCancel,
    isEditing = false,
    loading: externalLoading = false,
    resource = null
}) => {
    const { t } = useTranslation();

    // Support both `data` (from common components) and `refill` (legacy)
    const modelData = data || refill;

    // Initialize form with validation rules
    const initialFormData = {
        quantity_added: modelData?.quantity_added || 1,
        notes: modelData?.notes || ''
    };

    const validationRules = {
        quantity_added: {
            required: true,
            custom: (value, formData) => {
                const numValue = parseInt(value) || 0;
                if (numValue <= 0) {
                    return t('refill.invalid_quantity') || 'Quantity must be greater than 0';
                }
                return null;
            }
        }
    };

    const {
        formData,
        loading,
        error,
        fieldErrors,
        handleSubmit,
        updateFormData,
        getFieldProps,
        handleError
    } = useCommonForm({
        initialData: initialFormData,
        onSubmit: async (submitData) => {
            const cleanedData = {
                quantity_added: submitData.quantity_added,
                notes: submitData.notes || null
            };

            await onSubmit(cleanedData);
        },
        externalLoading,
        validationRules
    });

    // Update form data when modelData changes
    useEffect(() => {
        if (modelData) {
            const newFormData = {
                quantity_added: modelData.quantity_added || 1,
                notes: modelData.notes || ''
            };
            updateFormData(newFormData);
        } else {
            updateFormData({
                quantity_added: 1,
                notes: ''
            });
        }
    }, [modelData]); // Removed updateFormData dependency

    // Custom handler for quantity field to ensure it's always a number
    const handleQuantityChange = (e) => {
        const value = parseInt(e.target.value) || 0;
        const syntheticEvent = {
            target: {
                name: 'quantity_added',
                value: value,
                type: 'number'
            }
        };
        // Use the form's handleChange but with processed value
        getFieldProps('quantity_added').onChange(syntheticEvent);
    };

    return (
        <form onSubmit={handleSubmit} className="refill-form">
            <ErrorDisplay error={error} fieldErrors={fieldErrors} />

            {resource && (
                <div className="resource-info">
                    <h4>{t('refill.resource')}: {resource.name}</h4>
                    <p>{resource.description}</p>
                    <p className="quantity-info">
                        {t('refill.current_quantity')}: {resource.current_quantity ?? resource.quantity ?? 0}
                    </p>
                </div>
            )}

            <div className="form-group">
                <label htmlFor="quantity_added">
                    {t('refill.quantity_to_add')} *
                </label>
                <IntegerInput
                    id="quantity_added"
                    name="quantity_added"
                    value={formData.quantity_added}
                    onChange={handleQuantityChange}
                    disabled={loading}
                    min={1}
                    className={fieldErrors.quantity_added ? 'error' : ''}
                    error={!!fieldErrors.quantity_added}
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="notes">
                    {t('refill.notes')}
                </label>
                <textarea
                    {...getFieldProps('notes')}
                    id="notes"
                    rows={3}
                    className="form-control"
                    placeholder={t('refill.notes_placeholder')}
                />
            </div>

            <div className="form-actions">
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="btn btn-secondary"
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </button>
                )}
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                >
                    {loading ? t('common.loading') : (isEditing ? t('common.save') : t('refill.add'))}
                </button>
            </div>
        </form>
    );
};

export default RefillForm;
